import { Page } from 'components/Page/Page';
import SummaryDetail, {
  SummaryData
} from 'components/SummaryDetail/SummaryDetail';
import { ChooseVendorStrategy } from 'core';
import { SiteAnnouncementDurationType, SiteAnnouncementFormData } from 'core/siteAnnouncement/SiteAnnouncement';
import i18n from 'i18n';
import { Fragment, useCallback, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { SiteAnnouncementContent } from 'components/SiteAnnouncement/SiteAnnouncementContent';

export type SiteAnnouncementSummaryProps = {
  siteAnnouncement: SiteAnnouncementFormData;
  goLast: () => void;
  onSubmit: (siteAnnouncement: SiteAnnouncementFormData) => void;
  onCancel: () => void;
};

export const SiteAnnouncementSummary: React.FC<
  SiteAnnouncementSummaryProps
> = ({ siteAnnouncement, goLast, onSubmit, onCancel }) => {
  const basicSettingSummary: SummaryData[] = useMemo(
    () => [
      {
        label: i18n.t<string>('siteAnnouncementForm.labels.name'),
        value: siteAnnouncement.name
      },
      {
        label: i18n.t<string>('siteAnnouncementForm.labels.content'),
        value: <SiteAnnouncementContent
          content={siteAnnouncement.content}
          macroToHyperlink={true}
        />
      },
      {
        label: i18n.t<string>('siteAnnouncementForm.labels.type'),
        value: i18n.t<string>(
          `siteAnnouncementForm.labels.types.${siteAnnouncement.type.toLowerCase()}`
        )
      },
      {
        label: i18n.t<string>('siteAnnouncementForm.labels.durationType'),
        // 常駐通知沒有天數（BE 存 null），只顯示時長類型
        value:
          siteAnnouncement.durationType === SiteAnnouncementDurationType.LIMITED
            ? i18n.t<string>('siteAnnouncementForm.labels.prefix.duration') +
              ' ' +
              siteAnnouncement.duration +
              ' ' +
              i18n.t<string>('siteAnnouncementForm.labels.postfix.duration', {
                plural: siteAnnouncement.duration > 1 ? 's' : ''
              })
            : i18n.t<string>('siteAnnouncementForm.labels.durationTypes.permanent')
      }
    ],
    [siteAnnouncement]
  );
  const vendorNumberSummary: SummaryData[] = useMemo(
    () => [
      {
        label: i18n.t<string>(
          'siteAnnouncementForm.labels.chooseVendorStrategy'
        ),
        value:
          siteAnnouncement.chooseVendorStrategy === ChooseVendorStrategy.ALL
            ? i18n.t<string>('siteAnnouncementForm.labels.allVendors')
            : `${i18n.t<string>(
                'siteAnnouncementForm.labels.partialVendors'
              )} ${siteAnnouncement.vendorNumbers.join(', ')}`
      }
    ],
    [siteAnnouncement]
  );

  const handleSubmit = useCallback(() => {
    onSubmit(siteAnnouncement);
  }, [siteAnnouncement, onSubmit]);

  const renderBtns = useCallback(() => {
    return (
      <Fragment>
        <Button size='sm' onClick={handleSubmit}>
          {i18n.t<string>('common.buttons.submit')}
        </Button>
        <Button size='sm' variant='secondary' onClick={goLast}>
          {i18n.t<string>('common.buttons.back')}
        </Button>
        <Button size='sm' variant='secondary' onClick={onCancel}>
          {i18n.t<string>('common.buttons.cancel')}
        </Button>
      </Fragment>
    );
  }, [handleSubmit, goLast, onCancel]);

  return (
    <Page.Content renderBtns={renderBtns}>
      <Page.Section title={i18n.t<string>('common.titles.checkYourSettings')}>
        <SummaryDetail data={basicSettingSummary} />
      </Page.Section>
      <Page.Section title={i18n.t<string>('siteAnnouncementForm.titles.vendorNumbersSection')}>
        <SummaryDetail data={vendorNumberSummary} />
      </Page.Section>
    </Page.Content>
  );
};
